import type { TextRun } from '@superdoc/contracts';
import type { PMNode } from '../../types.js';
import { textNodeToRun } from './text-run.js';
import { type InlineConverterParams } from './common.js';

/**
 * Converts a `bookmarkStart` PM node.
 *
 * Primary job: record the bookmark's PM position in the `bookmarks` Map so
 * cross-reference navigation (goToAnchor) can resolve `#<bookmark-name>`
 * hrefs to a document position.
 *
 * SD-2454: when `converterContext.showBookmarks` is true, also emit a visible
 * gray `[` marker at the bookmark start, matching Word's opt-in "Show
 * bookmarks" feature. The marker is a regular TextRun so it flows through
 * pagination and line breaking like any other character; `dataAttrs` tag it
 * so DomPainter can style it gray and set a tooltip with the bookmark name.
 *
 * When `showBookmarks` is false (the default), the converter still descends
 * into any content inside the bookmark span but emits no visual output.
 */
export function bookmarkStartNodeToBlocks(params: InlineConverterParams): TextRun | void {
  const {
    node,
    positions,
    bookmarks,
    visitNode,
    inheritedMarks,
    sdtMetadata,
    runProperties,
    inlineRunProperties,
    converterContext,
  } = params;
  const nodeAttrs =
    typeof node.attrs === 'object' && node.attrs !== null ? (node.attrs as Record<string, unknown>) : {};
  const bookmarkName = typeof nodeAttrs.name === 'string' ? nodeAttrs.name : undefined;

  if (bookmarkName && bookmarks) {
    const nodePos = positions.get(node);
    if (nodePos) {
      bookmarks.set(bookmarkName, nodePos.start);
    }
  }

  // Word hides `_Toc…` / `_Ref…` / other `_`-prefixed bookmarks from its Show
  // Bookmarks rendering because they're autogenerated (headings, fields).
  // Mirror that so opt-in markers don't pollute every heading and xref target.
  const shouldRender =
    converterContext?.showBookmarks === true && typeof bookmarkName === 'string' && !bookmarkName.startsWith('_');

  let run: TextRun | undefined;
  if (shouldRender) {
    run = textNodeToRun({
      ...params,
      node: { type: 'text', text: '[', marks: [...(node.marks ?? [])] } as PMNode,
    });
    run.dataAttrs = {
      ...(run.dataAttrs ?? {}),
      'data-bookmark-name': bookmarkName!,
      'data-bookmark-marker': 'start',
    };
    // Record the id so the matching bookmarkEnd converter knows to emit `]`.
    // Without this, suppressing a `_`-prefixed start leaves an orphan `]`.
    const bookmarkIdRaw = nodeAttrs.id;
    const bookmarkId =
      typeof bookmarkIdRaw === 'string' || typeof bookmarkIdRaw === 'number' ? String(bookmarkIdRaw) : '';
    if (bookmarkId && converterContext?.renderedBookmarkIds) {
      converterContext.renderedBookmarkIds.add(bookmarkId);
    }
  }

  if (Array.isArray(node.content)) {
    // SD-2781: forward inlineRunProperties so text inside the bookmark span
    // preserves run-level bidi/script metadata from the enclosing run wrapper.
    node.content.forEach((child) =>
      visitNode(child, inheritedMarks, sdtMetadata, runProperties, false, inlineRunProperties),
    );
  }

  return run;
}
